/**
 * Pure pieces of the Trustpilot micro TrustBox facade: the copy it prints and
 * the signal that the real widget is about to paint and can take over. Kept
 * free of React and the DOM so they are unit-tested (trustpilot-facade.test.ts).
 */

/** The origin every TrustBox iframe is served from. */
export const TRUSTBOX_ORIGIN = 'https://widget.trustpilot.com';

/**
 * The last message a TrustBox iframe posts, right after `ping`, as a JSON
 * string: `{"command":"impression","widgetId":4}`. It is the only signal the
 * frame gives, and it comes just BEFORE the rating paints: 20-45 ms before on
 * the micro template, on a fast line and on a 400 ms-latency one alike
 * (measured Oct 19 2026). Hence {@link TRUSTBOX_PAINT_SETTLE_MS}.
 */
export const TRUSTBOX_IMPRESSION_COMMAND = 'impression';

/**
 * How long the facade stays up after the impression, so the swap lands on a
 * painted widget. Over 20x the measured gap; the facade is pixel-identical,
 * so holding it longer costs nothing a visitor can see.
 */
export const TRUSTBOX_PAINT_SETTLE_MS = 1000;

/**
 * The query the micro TrustBox puts on its profile link, so a click on the
 * facade is attributed to the TrustBox exactly like a click on the widget.
 */
const MICRO_PROFILE_QUERY = 'utm_medium=trustbox&utm_source=MicroTrustScore';

export interface TrustpilotRating {
  /** Word Trustpilot prints for the score, e.g. "Excellent". */
  label: string;
  /** TrustScore to one decimal, e.g. "5.0". */
  score: string;
  outOf: string;
  reviewsUrl: string;
}

export interface MicroFacadeCopy {
  label: string;
  /** "5.0 out of 5", worded as the widget words it. */
  rating: string;
  href: string;
}

export function microFacadeCopy({ label, score, outOf, reviewsUrl }: TrustpilotRating): MicroFacadeCopy {
  const separator = reviewsUrl.includes('?') ? '&' : '?';
  return {
    label,
    rating: `${score} out of ${outOf}`,
    href: `${reviewsUrl}${separator}${MICRO_PROFILE_QUERY}`,
  };
}

export interface FrameMessage {
  origin: string;
  data: unknown;
  source: unknown;
}

const commandOf = (data: unknown): unknown => {
  let payload = data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return undefined;
    }
  }
  return typeof payload === 'object' && payload !== null && 'command' in payload ? payload.command : undefined;
};

/** True when `message` is `frame` (one TrustBox iframe) posting its impression. */
export function isTrustboxImpression(message: FrameMessage, frame: unknown): boolean {
  if (message.origin !== TRUSTBOX_ORIGIN || frame == null || message.source !== frame) return false;
  return commandOf(message.data) === TRUSTBOX_IMPRESSION_COMMAND;
}
